import { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/auto_carousel.css';

const AutoCarousel = ({ images = [], interval = 4000, title, className = '' }) => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [isPortrait, setIsPortrait] = useState(false);
  const timerRef = useRef(null);

  const next = useCallback(() => {
    setCurrent(c => (c + 1) % images.length);
  }, [images.length]);

  const prev = useCallback(() => {
    setCurrent(c => (c - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (paused || images.length < 2) return;
    timerRef.current = setInterval(next, interval);
    return () => clearInterval(timerRef.current);
  }, [paused, next, interval, images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox !== null) {
        if (e.key === 'ArrowRight') setLightbox(l => (l + 1) % images.length);
        if (e.key === 'ArrowLeft') setLightbox(l => (l - 1 + images.length) % images.length);
        if (e.key === 'Escape') setLightbox(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, images.length]);

  useEffect(() => {
    if (!images.length) return;
    let loaded = 0;
    let portrait = 0;
    images.forEach(src => {
      const img = new Image();
      img.onload = () => {
        if (img.naturalHeight > img.naturalWidth) portrait++;
        loaded++;
        if (loaded === images.length) {
          setIsPortrait(portrait / images.length >= 0.8);
        }
      };
      img.onerror = () => {
        loaded++;
        if (loaded === images.length) {
          setIsPortrait(portrait / images.length >= 0.8);
        }
      };
      img.src = src;
    });
  }, [images]);

  if (!images.length) return null;

  return (
    <section className={`auto-carousel ${className}`}>
      {title && <h2 className="auto-carousel-title">{title}</h2>}
      <div
        className={`auto-carousel-track${isPortrait ? ' auto-carousel-track--portrait' : ''}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {images.map((src, i) => (
          <div
            key={i}
            className={`auto-carousel-slide ${i === current ? 'active' : i === (current - 1 + images.length) % images.length ? 'prev' : ''}`}
            onClick={() => setLightbox(i)}
          >
            <img src={src} alt={`Фото ${i + 1}`} loading="lazy" />
          </div>
        ))}

        {images.length > 1 && (
          <>
            <button className="auto-carousel-arrow auto-carousel-arrow--left" onClick={e => { e.stopPropagation(); prev(); }}>‹</button>
            <button className="auto-carousel-arrow auto-carousel-arrow--right" onClick={e => { e.stopPropagation(); next(); }}>›</button>
            <div className="auto-carousel-dots">
              {images.map((_, i) => (
                <button
                  key={i}
                  className={`auto-carousel-dot ${i === current ? 'active' : ''}`}
                  onClick={e => { e.stopPropagation(); setCurrent(i); }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {lightbox !== null && (
        <div className="auto-carousel-lightbox" onClick={() => setLightbox(null)}>
          <button className="auto-carousel-lb-close" onClick={() => setLightbox(null)}>✕</button>
          <button className="auto-carousel-lb-arrow auto-carousel-lb-arrow--left" onClick={e => { e.stopPropagation(); setLightbox(l => (l - 1 + images.length) % images.length); }}>‹</button>
          <div className="auto-carousel-lb-img-wrap" onClick={e => e.stopPropagation()}>
            <img src={images[lightbox]} alt="" />
          </div>
          <button className="auto-carousel-lb-arrow auto-carousel-lb-arrow--right" onClick={e => { e.stopPropagation(); setLightbox(l => (l + 1) % images.length); }}>›</button>
          <div className="auto-carousel-lb-counter">{lightbox + 1} / {images.length}</div>
        </div>
      )}
    </section>
  );
};

export default AutoCarousel;
